import { lazy } from 'react'
import { BrowserRouter, Route, Routes as RouterRoutes, Navigate } from 'react-router-dom'
import AppLandingLayout from 'containers/App/AppLandingLayout'
import AppRegisterLayout from 'containers/App/AppRegisterLayout'
import AppMaintenanceLayout from 'containers/App/AppMaintenanceLayout'
import AppConciergeLayout from 'containers/App/AppConciergeLayout'

const AppCompanySettingsLayout = lazy(() => import('containers/App/AppCompanySettingsLayout'))
const AppCompanySettingsLayoutV2 = lazy(() => import('containers/App/AppCompanySettingsLayoutV2'))
const AppLayout = lazy(() => import('containers/App/AppLayout'))
const AppLayoutV2 = lazy(() => import('containers/App/AppLayoutV2'))
const AppUserLayout = lazy(() => import('containers/App/AppUserLayout'))
const AppUserSettingsLayout = lazy(() => import('containers/App/AppUserSettingsLayout'))
const AppResponsePlanLayout = lazy(() => import('containers/App/AppResponsePlanLayout'))
import ProtectedRoutesByRegister from './protectedByRegister'
import ProtectedRoutesByUser from './protectedByUser'
import ProtectedRoutesByMaintenance from './protectedByMaintenance'
import { EUserRole } from 'models/user.model'
import { ErrorBoundary } from 'react-error-boundary'
import InternalError from 'ui/src/pages/common/InternalError'
import { useFlags } from 'launchdarkly-react-client-sdk'
import ProtectedByEmailVerification from './protectedByEmailVerification'
import ProtectByResponsePlan from './protectByResponsePlan'
import ProtectByResponsePlanByUser from './protectByResponsePlanByUser'
import RedirectRoute from './redirectRoute'

// Lazy load settings pages
const AdminUserManagement = lazy(() => import('pages/companyV2/Settings/AdminUserManagement'))
const Security = lazy(() => import('pages/companyV2/Settings/Security'))

// **** Public ****
const SignIn = lazy(() => import('pages/public/SignIn'))
const SignUp = lazy(() => import('pages/public/SignUp'))
const Invite = lazy(() => import('pages/public/Invite'))
const RegisterInvite = lazy(() => import('pages/companyV2/RegisterInviteForm'))
const AdminRegisterInvite = lazy(() => import('pages/companyV2/AdminRegisterInvite'))
const ContactUs = lazy(() => import('pages/public/ContactUs'))
const ForgotPassword = lazy(() => import('pages/public/ForgotPassword'))
import ExternalUrlRedirect from 'pages/public/ExternalUrlRedirect'

// **** User ****
const UserWelcome = lazy(() => import('pages/user/MessagePages/Welcome'))
const AlmostThere = lazy(() => import('pages/user/MessagePages/AlmostThere'))
const GetStarted = lazy(() => import('pages/user/MessagePages/GetStarted'))
const PaymentFailed = lazy(() => import('pages/user/MessagePages/PaymentFailed'))

const UserEmailLeaks = lazy(
  () =>
    import(
      'pages/user/Dashboard/components/UserThreatsAreas/Areas/CriticalArea/pages/EmailLeaksPage'
    ),
)
const UserPhoneLeaks = lazy(
  () =>
    import('pages/user/Dashboard/components/UserThreatsAreas/Areas/ImportantArea/pages/PhoneLeaks'),
)
const UserNameLeaks = lazy(
  () =>
    import('pages/user/Dashboard/components/UserThreatsAreas/Areas/ImportantArea/pages/NameLeaks'),
)
const UserAddressLeaks = lazy(
  () =>
    import(
      'pages/user/Dashboard/components/UserThreatsAreas/Areas/CriticalArea/pages/AddressLeaks'
    ),
)

const UserImageLeaks = lazy(
  () =>
    import('pages/user/Dashboard/components/UserThreatsAreas/Areas/ImportantArea/pages/ImageLeaks'),
)
const UserMarketplace = lazy(() => import('pages/user/Marketplace'))
const UserDashboard = lazy(() => import('pages/user/Dashboard'))
const UserDashboardV2 = lazy(() => import('pages/userV2/Dashboard'))
const UserFindings = lazy(() => import('pages/userV2/Findings'))
const UserSettings = lazy(() => import('pages/user/Settings'))
const UserSubsciption = lazy(() => import('pages/user/Subscription'))
const UserAccountSettings = lazy(() => import('pages/user/AccountSettings'))
const UserLoadingScore = lazy(() => import('pages/user/LoadingScore'))
const UserUserForm = lazy(() => import('pages/user/CreateUser'))

// **** Company ****
const CompanyDashboard = lazy(() => import('pages/company/Dashboard'))
const CompanyDashboardV2 = lazy(() => import('pages/companyV2/Dashboard'))
const CompanyBatchSignUp = lazy(() => import('pages/companyV2/SignUp'))
const CompanyUsers = lazy(() => import('pages/company/Users'))
const CompanyIntegration = lazy(() => import('pages/companyV2/Integration'))
const CompanyUsersV2 = lazy(() => import('pages/companyV2/Users'))
const CompanyFindings = lazy(() => import('pages/companyV2/Findings'))
const CompanyBulkScans = lazy(() => import('pages/companyV2/BulkScans'))
const CompanyUserDetails = lazy(() => import('pages/companyV2/Users/pages/UserDetails'))
const CompanyExecutiveDetails = lazy(() => import('pages/companyV2/Users/pages/ExecutiveDetails'))
const CompanyDepartmentDetails = lazy(() => import('pages/companyV2/Users/pages/DepartmentDetails'))
const CompanySettings = lazy(() => import('pages/company/Settings/AccountSettings'))
const CompanyProfile = lazy(() => import('pages/company/Settings/Profile'))
const CompanyAdminProfileSettings = lazy(
  () => import('pages/company/Settings/AdminProfileSettings'),
)
const CompanyAdminWelcome = lazy(() => import('pages/company/Admin/WelcomePage'))
const CompanyAdminWelcomeV2 = lazy(() => import('pages/companyV2/WelcomeAdminPage'))
const CompanyBatchWelcome = lazy(() => import('pages/companyV2/WelcomePage'))
const CompanyInvalidInvite = lazy(() => import('pages/company/InvalidInvite'))
const CreateBatch = lazy(() => import('pages/company/CreateBatch'))
const Training = lazy(() => import('pages/companyV2/Training'))
const BatchLoading = lazy(() => import('pages/companyV2/BatchLoading'))
const CompanySignUpV2 = lazy(() => import('pages/companyV2/UserSignUp'))
const CompanySettingsV2 = lazy(() => import('pages/companyV2/Settings/Profile'))

// **** CyberMatch ****
const CyberMatchJobSeeker = lazy(() => import('pages/cybermatch/JobSeeker'))
const CyberMatchProfile = lazy(() => import('pages/cybermatch/Profile'))
const CyberMatchJobSearch = lazy(() => import('pages/cybermatch/JobSearch'))
const CyberMatchMyJobs = lazy(() => import('pages/cybermatch/MyJobs'))
const CyberMatchMyLearning = lazy(() => import('pages/cybermatch/MyLearning'))

// **** Common ****
const NotFound = lazy(() => import('ui/src/pages/common/NotFound'))
const ThankYou = lazy(() => import('pages/common/ThankYou'))
const VerifyingEmail = lazy(() => import('pages/common/VerifyingEmail'))
const Maintenance = lazy(() => import('ui/src/pages/common/Maintenance'))
const ResponsePlanIntroduction = lazy(() => import('pages/common/ResponsePlan/Introduction'))
const ResponsePlanForm = lazy(() => import('pages/user/ResponsePlanForm'))
const ResponsePlanOverview = lazy(() => import('pages/user/ResponsePlanOverview'))
const ResponsePlanAccess = lazy(() => import('pages/public/ResponsePlanAccess'))
const TermsOfService = lazy(() => import('ui/src/pages/common/ToS'))
const PrivacyPolicy = lazy(() => import('ui/src/pages/common/Privacy'))
const SalesforceConcierge = lazy(() => import('pages/common/SalesforceConcierge/ConciergeForm'))
const SalesforceConciergeThankyou = lazy(
  () => import('pages/common/SalesforceConcierge/ConciergeThankyou'),
)

const Routes = () => {
  const flags = useFlags()

  const allowedRole = flags.featureB2CRoutes
    ? [EUserRole.USER_B2C, EUserRole.EMPLOYEE, EUserRole.ADMIN]
    : [EUserRole.EMPLOYEE, EUserRole.ADMIN]

  return (
    <BrowserRouter>
      <ErrorBoundary
        fallbackRender={({ error, resetErrorBoundary }) => (
          <InternalError resetErrorBoundary={resetErrorBoundary} error={error} />
        )}
      >
        <RouterRoutes>
          <Route element={<ProtectedRoutesByMaintenance />}>
            {/* // PUBLIC ROUTERS */}
            <Route element={<AppConciergeLayout />}>
              <Route path="concierge" element={<SalesforceConcierge />} />
              <Route path="thank-you" element={<SalesforceConciergeThankyou />} />
            </Route>
            <Route
              path="dental"
              element={<ExternalUrlRedirect url="https://dental.protexxa.com/" />}
            />
            <Route
              path="nonprofit"
              element={<ExternalUrlRedirect url="https://nonprofits.protexxa.com/" />}
            />
            <Route
              path="nonprofits"
              element={<ExternalUrlRedirect url="https://nonprofits.protexxa.com/" />}
            />
            <Route path="legal/tos" element={<TermsOfService />} />
            <Route path="legal/privacy" element={<PrivacyPolicy />} />
            <Route element={<AppLandingLayout />}>
              <Route index element={<SignIn />} />
            </Route>
            <Route path="redirect" element={<RedirectRoute />} />
            <Route element={<AppLandingLayout />}>
              {<Route index path="signin" element={<SignIn />} />}
              <Route path="forgotpassword" element={<ForgotPassword />} />
              {/* Reponse plan routes */}
              <Route path="responseplan" element={<ResponsePlanAccess />} />
            </Route>

            <Route element={<AppLayout />}>
              <Route element={<ProtectByResponsePlan />}>
                <Route path="response-plan/dashboard" element={<ResponsePlanOverview />} />
                <Route path="response-plan/contacts" element={<ResponsePlanOverview />} />
                <Route path="response-plan/banks" element={<ResponsePlanOverview />} />
              </Route>
            </Route>

            <Route element={<AppRegisterLayout />}>
              <Route path="contact-us" element={<ContactUs />} />
              <Route path="thankyou" element={<ThankYou />} />
              <Route path="invite" element={<Invite />} />
              <Route path="invite/:code" element={<RegisterInvite />} />
              <Route path="admin/invite/:code" element={<AdminRegisterInvite />} />
              {flags.featureB2CRoutes && <Route path="signup" element={<SignUp />} />}
            </Route>
            {/* // USER ROUTERS */}
            <Route path="/">
              {/* PROTECT BY USER */}
              <Route element={<ProtectedRoutesByUser allowedRole={allowedRole} />}>
                {(flags.primaryEmailVerification || flags.additionalEmailVerification) && (
                  <Route path="email/verify/:email/:code" element={<VerifyingEmail />} />
                )}
                <Route element={<ProtectedRoutesByRegister status="pending" />}>
                  <Route element={<ProtectedByEmailVerification />}>
                    <Route path="almost-there" element={<AlmostThere />} />
                    <Route path="get-started" element={<GetStarted />} />
                    <Route path="payment-failed" element={<PaymentFailed />} />
                    <Route path="welcome" element={<UserWelcome />} />
                  </Route>
                </Route>

                <Route path="userform" element={<UserUserForm />} />

                <Route path="loading-score" element={<UserLoadingScore />} />

                {/* PROTECT BY REGISTER COMPLETED */}
                <Route element={<ProtectedRoutesByRegister status="completed" />}>
                  <Route path="thankyou" element={<ThankYou />} />
                  <Route element={<AppUserLayout />}>
                    <Route
                      path="dashboard"
                      element={flags.userDashboardV2 ? <UserDashboardV2 /> : <UserDashboard />}
                    />
                    {flags.userDashboardV2 && <Route path="findings" element={<UserFindings />} />}
                    {flags.cyberMatch === true && (
                      <Route path="cybermatch" element={<CyberMatchJobSeeker />} />
                    )}
                    {flags.cyberMatch === true && (
                      <Route path="cybermatch/profile" element={<CyberMatchProfile />} />
                    )}
                    {flags.cyberMatch === true && (
                      <Route path="cybermatch/jobs/search" element={<CyberMatchJobSearch />} />
                    )}
                    {flags.cyberMatch === true && (
                      <Route path="cybermatch/jobs/my-jobs" element={<CyberMatchMyJobs />} />
                    )}
                    {flags.cyberMatch === true && (
                      <Route path="cybermatch/learning" element={<CyberMatchMyLearning />} />
                    )}
                    <Route path="marketplace" element={<UserMarketplace />} />
                    <Route path="email-leaks" element={<UserEmailLeaks />} />
                    <Route path="phone-leaks" element={<UserPhoneLeaks />} />
                    <Route path="name-leaks" element={<UserNameLeaks />} />
                    <Route path="image-leaks" element={<UserImageLeaks />} />
                    <Route path="address-leaks" element={<UserAddressLeaks />} />
                    <Route element={<AppUserSettingsLayout />}>
                      <Route path="account-settings" element={<UserAccountSettings />} />
                      <Route path="profile" element={<UserSettings />} />
                      <Route path="subscription" element={<UserSubsciption />} />
                    </Route>
                  </Route>

                  <Route element={<AppResponsePlanLayout />}>
                    <Route element={<ProtectByResponsePlanByUser />}>
                      <Route
                        path="response-plan/introduction"
                        element={<ResponsePlanIntroduction />}
                      />
                      <Route path="response-plan/form" element={<ResponsePlanForm />} />
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Route>

            {/* // COMPANY ROUTERS */}
            <Route path="company">
              <Route element={<AppRegisterLayout companyInvite={true} />}>
                <Route path="batch/:v2" element={<CompanyBatchSignUp />} />
                <Route path="batch/uuid/:uuid" element={<CompanyBatchSignUp />} />
                <Route path=":jwtToken" element={<CompanySignUpV2 />} />
                <Route path="uuid/:uuid" element={<CompanySignUpV2 />} />
              </Route>
              {/* Shared error page for both V1 and V2 - works for all company invite validation failures */}
              <Route path="invalid-invite" element={<CompanyInvalidInvite />} />

              <Route element={<ProtectedRoutesByUser allowedRole={[EUserRole.ADMIN]} />}>
                <Route element={<ProtectedByEmailVerification />}>
                  <Route path="welcome" element={<CompanyAdminWelcome />} />
                  <Route path="batch/welcome" element={<CompanyBatchWelcome />} />
                  <Route path="admin/welcome" element={<CompanyAdminWelcomeV2 />} />
                  <Route path="onboarding" element={<CreateBatch />} />
                  <Route path="batch-loading" element={<BatchLoading />} />
                  <Route element={flags.dashboardV2 ? <AppLayoutV2 /> : <AppLayout />}>
                    <Route
                      path="dashboard"
                      element={flags.dashboardV2 ? <CompanyDashboardV2 /> : <CompanyDashboard />}
                    />
                    {flags.cyberMatchEmployer !== false && (
                      <Route path="cybermatch" element={<CyberMatchJobSeeker />} />
                    )}
                    {flags.dashboardV2 && <Route path="findings" element={<CompanyFindings />} />}
                    {flags.dashboardV2 && <Route path="users" element={<CompanyUsersV2 />} />}
                    {flags.dashboardV2 && (
                      <Route path=":companyId/users/:registerId" element={<CompanyUserDetails />} />
                    )}
                    {flags.dashboardV2 && (
                      <Route
                        path="users/executives/:registerId"
                        element={<CompanyExecutiveDetails />}
                      />
                    )}
                    {flags.dashboardV2 && (
                      <Route
                        path=":companyId/departments/:departmentId"
                        element={<CompanyDepartmentDetails />}
                      />
                    )}
                    {flags.dashboardV2NoneMvp && (
                      <Route path="integration" element={<CompanyIntegration />} />
                    )}
                    {flags.dashboardV2 && <Route path="scan-logs" element={<CompanyBulkScans />} />}
                    <Route path="marketplace" element={<UserMarketplace />} />
                    <Route
                      element={
                        flags.appSettingsLayoutV2 ? (
                          <AppCompanySettingsLayoutV2 />
                        ) : (
                          <AppCompanySettingsLayout />
                        )
                      }
                    >
                      <Route
                        path="settings"
                        element={
                          flags.appSettingsLayoutV2 ? <CompanySettingsV2 /> : <CompanySettings />
                        }
                      />
                      {!flags.appSettingsLayoutV2 && (
                        <Route path="profile-settings" element={<CompanyProfile />} />
                      )}
                      {!flags.appSettingsLayoutV2 && (
                        <Route
                          path="individual-settings"
                          element={<CompanyAdminProfileSettings />}
                        />
                      )}
                      {flags.appSettingsLayoutV2 && (
                        <Route
                          path="admin-user-management-settings"
                          element={<AdminUserManagement />}
                        />
                      )}
                      {flags.appSettingsLayoutV2 && (
                        <Route path="security-settings" element={<Security />} />
                      )}
                      {!flags.dashboardV2 && !flags.appSettingsLayoutV2 && (
                        <Route path="users-settings" element={<CompanyUsers />} />
                      )}
                      {/* Redirect deprecated billing settings route */}
                      <Route
                        path="billing-settings"
                        element={<Navigate to="/company/settings" replace />}
                      />
                    </Route>
                    {/* Analytics Overview deprecated - redirect to dashboard */}
                    <Route
                      path="analytics/overview"
                      element={<Navigate to="/company/dashboard" replace />}
                    />
                    {flags.dashboardV2NoneMvp && <Route path="training" element={<Training />} />}
                  </Route>
                </Route>
              </Route>
            </Route>

            {/* // NOT FOUND ROUTER */}
            <Route path="*" element={<NotFound />} />
          </Route>
          {/* // MAINTENANCE ROUTE */}
          {flags.maintenance && <Route path="maintenance" element={<Maintenance />} />}
          {flags.maintenance && (
            <Route element={<AppMaintenanceLayout />}>
              <Route path="contact" element={<ContactUs />} />
            </Route>
          )}
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  )
}
export default Routes
